import { Html, Line } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useSpaceStore } from "../../stores";
import type { CelestialObject, PlanetData, StarSystem } from "../../types";
import { getStarColor, getSystemStarSize } from "../../types";
import { AmbientColorPatches } from "./AmbientColorPatches";
import { NebulaBackground } from "./NebulaBackground";
import { ParallaxStars } from "./ParallaxStars";
import { getPlanetGlowTexture, getPlanetTexture, getSystemStarTexture } from "./textures";
import { screenToWorld, useViewportSync } from "./useViewportSync";

// --- Constants ---

/** Minimum pixel distance from star center for the innermost orbit */
const MIN_ORBIT_PX = 55;
/** Target pixel distance from star center for the outermost orbit */
const TARGET_OUTER_PX = 220;
const HIT_RADIUS_MOUSE = 16;
const HIT_RADIUS_TOUCH = 32;
const FLARE_RAYS = 6;
/** Y-axis compression to simulate a tilted orbital plane (0 = edge-on, 1 = top-down) */
const TILT = 0.4;

/**
 * Non-linear orbit→pixel mapper.  Uses sqrt scaling so inner planets are
 * pushed away from the star (clearing the glow) while outer planets compress
 * inward — keeps everything visible and clickable.
 */
function createOrbitMapper(planets: PlanetData[]): (au: number) => number {
	if (planets.length === 0) return (au) => MIN_ORBIT_PX + au * 60;
	const maxRadius = Math.max(...planets.map((p) => p.orbitalRadius));
	if (maxRadius <= 0) return (au) => MIN_ORBIT_PX + au * 60;

	const range = TARGET_OUTER_PX - MIN_ORBIT_PX;
	return (au: number): number => {
		if (au <= 0) return 0;
		return MIN_ORBIT_PX + Math.sqrt(au / maxRadius) * range;
	};
}

function computePlanetPosition(
	planet: PlanetData,
	now: number,
	orbitMapper: (au: number) => number,
): { px: number; py: number; depth: number; dotRadius: number } {
	const angle = (now / (planet.orbitalPeriod * 100)) * Math.PI * 2;
	const radius = orbitMapper(planet.orbitalRadius);
	const px = Math.cos(angle) * radius;
	const py = Math.sin(angle) * radius * TILT;
	const depth = Math.sin(angle); // -1 (front/bottom) to +1 (back/top)
	const dotRadius = Math.max(3, Math.min(8, planet.radius * 0.7));
	return { px, py, depth, dotRadius };
}

// --- Sub-components ---

function SystemStar({ system }: { system: StarSystem }): React.JSX.Element {
	const flareGroupRef = useRef<THREE.Group>(null);
	const coronaRef = useRef<THREE.Mesh>(null);
	const color = getStarColor(system.star.spectralClass);
	const size = getSystemStarSize(system.star.absoluteMagnitude);

	const starTexture = useMemo(
		() => getSystemStarTexture(system.star.spectralClass, size),
		[system.star.spectralClass, size],
	);

	// Flare ray Three.js Line objects
	const flareObjects = useMemo(() => {
		const objects: THREE.Line[] = [];
		const mat = new THREE.LineBasicMaterial({
			color,
			transparent: true,
			opacity: 0.06,
		});
		for (let i = 0; i < FLARE_RAYS; i++) {
			const geo = new THREE.BufferGeometry().setFromPoints([
				new THREE.Vector3(0, 0, 0),
				new THREE.Vector3(1, 0, 0),
			]);
			objects.push(new THREE.Line(geo, mat));
		}
		return objects;
	}, [color]);

	// Animate flares + corona
	useFrame(({ clock }) => {
		const time = clock.getElapsedTime() * 1000;
		if (flareGroupRef.current) {
			for (let i = 0; i < FLARE_RAYS; i++) {
				const child = flareGroupRef.current.children[i];
				if (!child) continue;
				const angle = (i / FLARE_RAYS) * Math.PI * 2 + time * 0.00008;
				const rayLen = size * (1.3 + 0.25 * Math.sin(time * 0.002 + i * 1.5));
				child.rotation.z = angle;
				child.scale.x = rayLen;
			}
		}
		if (coronaRef.current) {
			const pulse = 1 + 0.06 * Math.sin(time * 0.002);
			coronaRef.current.scale.setScalar(pulse);
		}
	});

	return (
		<group>
			{/* Star glow sprite */}
			<sprite scale={[size * 2 + 2, size * 2 + 2, 1]}>
				<spriteMaterial map={starTexture} transparent depthWrite={false} />
			</sprite>

			{/* Lens flare rays */}
			<group ref={flareGroupRef}>
				{flareObjects.map((obj, i) => (
					<primitive key={`flare-${String(i)}`} object={obj} />
				))}
			</group>

			{/* Corona pulse ring */}
			<mesh ref={coronaRef}>
				<ringGeometry args={[size * 0.82, size * 0.88, 64]} />
				<meshBasicMaterial color={color} transparent opacity={0.07} side={THREE.DoubleSide} />
			</mesh>
		</group>
	);
}

function SystemHabitableZone({ orbitMapper }: { orbitMapper: (au: number) => number }): React.JSX.Element {
	const innerR = orbitMapper(0.8);
	const outerR = orbitMapper(1.5);
	return (
		<group scale={[1, TILT, 1]}>
			<mesh renderOrder={-1}>
				<ringGeometry args={[innerR, outerR, 64]} />
				<meshBasicMaterial color={0x34d399} transparent opacity={0.05} side={THREE.DoubleSide} />
			</mesh>
		</group>
	);
}

function SystemOrbitalRings({
	planets,
	orbitMapper,
}: { planets: PlanetData[]; orbitMapper: (au: number) => number }): React.JSX.Element {
	const ringPoints = useMemo(() => {
		return planets.map((planet) => {
			const r = orbitMapper(planet.orbitalRadius);
			const segments = 128;
			const pts: [number, number, number][] = [];
			for (let i = 0; i <= segments; i++) {
				const angle = (i / segments) * Math.PI * 2;
				pts.push([Math.cos(angle) * r, Math.sin(angle) * r * TILT, 0]);
			}
			return { planet, pts };
		});
	}, [planets, orbitMapper]);

	return (
		<group renderOrder={-1}>
			{ringPoints.map(({ planet, pts }) => (
				<Line
					key={planet.id}
					points={pts}
					color={planet.color}
					transparent
					opacity={0.19}
					lineWidth={1}
					dashed
					dashSize={4}
					gapSize={8}
					renderOrder={-2}
					depthWrite={false}
				/>
			))}
		</group>
	);
}

function PlanetSprite({
	color,
	isGasGiant,
	size,
}: { color: string; isGasGiant: boolean; size: number }): React.JSX.Element {
	const tex = useMemo(
		() => getPlanetTexture(color, isGasGiant),
		[color, isGasGiant],
	);
	return (
		<sprite scale={[size, size, 1]} renderOrder={2}>
			<spriteMaterial map={tex} transparent depthWrite={false} depthTest={false} />
		</sprite>
	);
}

function SystemPlanets({
	planets,
	selectedId,
	hoveredId,
	orbitMapper,
}: {
	planets: PlanetData[];
	selectedId: string | null;
	hoveredId: string | null;
	orbitMapper: (au: number) => number;
}): React.JSX.Element {
	const groupRefs = useRef<(THREE.Group | null)[]>([]);

	// Animate planet group positions with depth ordering
	useFrame(() => {
		const now = Date.now();
		for (let i = 0; i < planets.length; i++) {
			const planet = planets[i];
			if (!planet) continue;
			const { px, py, depth } = computePlanetPosition(planet, now, orbitMapper);
			const group = groupRefs.current[i];
			if (group) {
				// Z: front planets (depth<0) get +z (closer to camera at z=100)
				group.position.set(px, -py, -depth * 2);
			}
		}
	});

	const glowTex = useMemo(() => getPlanetGlowTexture(), []);

	return (
		<group>
			{planets.map((planet, i) => {
				const dotRadius = Math.max(3, Math.min(8, planet.radius * 0.7));
				const isSelected = planet.id === selectedId;
				const isHovered = planet.id === hoveredId;
				const isGasGiant = planet.radius >= 3.5;
				const spriteSize = dotRadius * 2;
				const glowSize = spriteSize * 1.8;

				return (
					<group key={planet.id}>
						{/* Planet group — moved each frame, children follow */}
						<group
							ref={(el) => {
								groupRefs.current[i] = el;
							}}
						>
							{/* Atmospheric glow (behind planet) */}
							<sprite
								scale={[glowSize, glowSize, 1]}
								position={[0, 0, -0.1]}
								renderOrder={1}
							>
								<spriteMaterial
									map={glowTex}
									color={planet.color}
									transparent
									opacity={isGasGiant ? 0.5 : 0.35}
									depthWrite={false}
									depthTest={false}
									blending={THREE.AdditiveBlending}
								/>
							</sprite>

							{/* Planet sphere */}
							<PlanetSprite
								color={planet.color}
								isGasGiant={isGasGiant}
								size={spriteSize}
							/>

							{/* Highlight ring */}
							{(isSelected || isHovered) && (
								<mesh renderOrder={3}>
									<ringGeometry
										args={[dotRadius + (isSelected ? 3 : 1), dotRadius + (isSelected ? 5 : 3), 32]}
									/>
									<meshBasicMaterial
										color={planet.color}
										transparent
										opacity={isSelected ? 0.5 : 0.3}
										side={THREE.DoubleSide}
										depthWrite={false}
										depthTest={false}
									/>
								</mesh>
							)}

							{/* Label */}
							<Html
								zIndexRange={[15, 0]}
								style={{
									color: "rgba(255,255,255,0.8)",
									fontSize: "10px",
									fontFamily: "Inter",
									pointerEvents: "none",
									whiteSpace: "nowrap",
									transform: `translate(${dotRadius + 4}px, 3px)`,
								}}
							>
								{planet.name}
							</Html>
						</group>

						{/* Trail arc — world-space, outside the moving group */}
						<PlanetTrail planet={planet} orbitMapper={orbitMapper} />
					</group>
				);
			})}
		</group>
	);
}

/** Animated trail ribbon for a single planet — fades and tapers behind the planet */
const TRAIL_ARC = 1.0; // ~57 degrees
const TRAIL_SEGMENTS = 48;
const TRAIL_BASE_WIDTH = 1.4;

const trailVertexShader = /* glsl */ `
	attribute float alpha;
	varying float vAlpha;
	void main() {
		vAlpha = alpha;
		gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
	}
`;

const trailFragmentShader = /* glsl */ `
	uniform vec3 uColor;
	varying float vAlpha;
	void main() {
		float a = vAlpha * vAlpha * 0.55;
		gl_FragColor = vec4(uColor, a);
	}
`;

function PlanetTrail({ planet, orbitMapper }: { planet: PlanetData; orbitMapper: (au: number) => number }): React.JSX.Element {
	const { geometry, posAttr } = useMemo(() => {
		const vertexCount = (TRAIL_SEGMENTS + 1) * 2;
		const positions = new Float32Array(vertexCount * 3);
		const alphas = new Float32Array(vertexCount);

		for (let i = 0; i <= TRAIL_SEGMENTS; i++) {
			const t = i / TRAIL_SEGMENTS;
			alphas[i * 2] = t;
			alphas[i * 2 + 1] = t;
		}

		const indices: number[] = [];
		for (let i = 0; i < TRAIL_SEGMENTS; i++) {
			const a = i * 2;
			const b = a + 1;
			const c = a + 2;
			const d = a + 3;
			indices.push(a, b, c, b, d, c);
		}

		const geo = new THREE.BufferGeometry();
		const posAttribute = new THREE.BufferAttribute(positions, 3);
		geo.setAttribute("position", posAttribute);
		geo.setAttribute("alpha", new THREE.BufferAttribute(alphas, 1));
		geo.setIndex(indices);
		return { geometry: geo, posAttr: posAttribute };
	}, []);

	useFrame(() => {
		const now = Date.now();
		const angle = (now / (planet.orbitalPeriod * 100)) * Math.PI * 2;
		const radius = orbitMapper(planet.orbitalRadius);
		const positions = posAttr.array as Float32Array;

		for (let s = 0; s <= TRAIL_SEGMENTS; s++) {
			const t = s / TRAIL_SEGMENTS;
			const a = angle - TRAIL_ARC + t * TRAIL_ARC;
			const cosA = Math.cos(a);
			const sinA = Math.sin(a);
			const width = TRAIL_BASE_WIDTH * t * t; // quadratic taper

			// Radial normal direction (outward from center), with Y tilt
			const cx = cosA * radius;
			const cy = -sinA * radius * TILT;
			// Width offset also tilted for consistent appearance
			const wxOuter = cosA * width;
			const wyOuter = -sinA * width * TILT;
			const depth = -sinA; // for z-ordering

			const idx = s * 6;
			positions[idx] = cx + wxOuter;
			positions[idx + 1] = cy + wyOuter;
			positions[idx + 2] = depth * 2 - 0.5;
			positions[idx + 3] = cx - wxOuter;
			positions[idx + 4] = cy - wyOuter;
			positions[idx + 5] = depth * 2 - 0.5;
		}

		posAttr.needsUpdate = true;
	});

	const material = useMemo(
		() =>
			new THREE.ShaderMaterial({
				transparent: true,
				depthWrite: false,
				uniforms: {
					uColor: { value: new THREE.Color(planet.color) },
				},
				vertexShader: trailVertexShader,
				fragmentShader: trailFragmentShader,
			}),
		[planet.color],
	);

	return <mesh geometry={geometry} material={material} renderOrder={-1} />;
}

// --- Helpers ---

function deriveSelectedId(obj: CelestialObject | null): string | null {
	if (!obj) return null;
	return obj.data.id;
}

function deriveObjectId(obj: CelestialObject | null): string | null {
	if (!obj) return null;
	return obj.data.id;
}

// --- Main SystemView Component ---

export function SystemView(): React.JSX.Element | null {
	const focusedSystem = useSpaceStore((s) => s.focusedSystem);
	const selectedObject = useSpaceStore((s) => s.selectedObject);
	const isSearchOpen = useSpaceStore((s) => s.isSearchOpen);
	const selectObject = useSpaceStore((s) => s.selectObject);
	const exitSystemView = useSpaceStore((s) => s.exitSystemView);
	const { gl } = useThree();

	const selectedId = deriveSelectedId(selectedObject);
	const hoveredPlanetRef = useRef<string | null>(null);

	// Camera sync
	useViewportSync();

	// ESC key handler
	useEffect(() => {
		function handleKeyDown(e: KeyboardEvent): void {
			if (e.key === "Escape" && !isSearchOpen) {
				exitSystemView();
			}
		}
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [exitSystemView, isSearchOpen]);

	// Non-linear orbit→pixel mapper for this system
	const orbitMapper = useMemo(
		() => (focusedSystem ? createOrbitMapper(focusedSystem.planets) : (au: number) => MIN_ORBIT_PX + au * 60),
		[focusedSystem],
	);

	// Hit testing for planets
	const findNearestPlanet = useCallback(
		(screenX: number, screenY: number, isTouch = false): CelestialObject | null => {
			if (!focusedSystem) return null;
			const { worldX, worldY } = screenToWorld(screenX, screenY);
			const hitRadius = isTouch ? HIT_RADIUS_TOUCH : HIT_RADIUS_MOUSE;
			const threshold = hitRadius / useSpaceStore.getState().systemView.scale;

			let nearest: CelestialObject | null = null;
			let minDist = threshold;

			// Check star at origin
			const starDist = Math.hypot(worldX, worldY);
			if (starDist < minDist) {
				minDist = starDist;
				nearest = { type: "star", data: focusedSystem };
			}

			// Check planets
			const now = Date.now();
			for (const planet of focusedSystem.planets) {
				const { px, py } = computePlanetPosition(planet, now, orbitMapper);
				const dist = Math.hypot(px - worldX, py - worldY);
				if (dist < minDist) {
					minDist = dist;
					nearest = {
						type: "planet",
						data: planet,
						system: focusedSystem,
					};
				}
			}

			return nearest;
		},
		[focusedSystem, orbitMapper],
	);

	// Pointer move for hover — skip on touch (tap handles selection)
	useEffect(() => {
		const canvas = gl.domElement;
		const handleMove = (e: PointerEvent) => {
			if (e.pointerType === "touch") return;

			const rect = canvas.getBoundingClientRect();
			const screenX = e.clientX - rect.left;
			const screenY = e.clientY - rect.top;
			const found = findNearestPlanet(screenX, screenY);
			const newId = deriveObjectId(found);

			if (newId !== hoveredPlanetRef.current) {
				hoveredPlanetRef.current = newId;
				canvas.style.cursor = newId ? "pointer" : "grab";
			}
		};
		canvas.addEventListener("pointermove", handleMove);
		return () => canvas.removeEventListener("pointermove", handleMove);
	}, [findNearestPlanet, gl.domElement]);

	// Click handler — uses larger hit radius for touch
	useEffect(() => {
		const canvas = gl.domElement;
		const handler = (e: Event) => {
			const detail = (e as CustomEvent).detail;
			const isTouch = detail.pointerType === "touch" || detail.pointerType === "pen";
			const found = findNearestPlanet(detail.screenX, detail.screenY, isTouch);
			selectObject(found);
		};
		canvas.addEventListener("canvas-click", handler);
		return () => canvas.removeEventListener("canvas-click", handler);
	}, [findNearestPlanet, selectObject, gl.domElement]);

	if (!focusedSystem) return null;

	return (
		<group>
			<NebulaBackground />
			<ParallaxStars />
			<AmbientColorPatches />
			<SystemHabitableZone orbitMapper={orbitMapper} />
			<SystemStar system={focusedSystem} />
			<SystemOrbitalRings planets={focusedSystem.planets} orbitMapper={orbitMapper} />
			<SystemPlanets
				planets={focusedSystem.planets}
				selectedId={selectedId}
				hoveredId={hoveredPlanetRef.current}
				orbitMapper={orbitMapper}
			/>
		</group>
	);
}
